import { useState, useMemo, useEffect } from 'react'

export interface PaginationState {
  page: number
  pageSize: number
  total: number
}

export interface UsePaginationOptions {
  initialPage?: number
  initialPageSize?: number
  pageSizeOptions?: number[]
}

export interface UsePaginationReturn {
  pagination: PaginationState
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  setPage: (page: number) => void
  setPageSize: (pageSize: number) => void
  setTotal: (total: number) => void
  pageCount: number
  paginationRange: number[]
  paginatedData: <T>(data: T[]) => T[]
}

/**
 * Custom hook for pagination logic
 */
export function usePagination(
  totalItems: number,
  options: UsePaginationOptions = {}
): UsePaginationReturn {
  const {
    initialPage = 1,
    initialPageSize = 25,
  } = options

  const [pagination, setPagination] = useState<PaginationState>({
    page: initialPage,
    pageSize: initialPageSize,
    total: totalItems,
  })

  // Update total when totalItems changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, total: totalItems }))
  }, [totalItems])

  const pageCount = useMemo(() => {
    return Math.ceil(pagination.total / pagination.pageSize)
  }, [pagination.total, pagination.pageSize])

  const paginationRange = useMemo(() => {
    const startPage = Math.max(1, pagination.page - 2)
    const endPage = Math.min(pageCount, pagination.page + 2)
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i)
  }, [pagination.page, pageCount])

  const paginatedData = <T,>(data: T[]): T[] => {
    return data.slice(
      (pagination.page - 1) * pagination.pageSize,
      pagination.page * pagination.pageSize
    )
  }

  const setPage = (page: number) => {
    setPagination((prev) => ({ ...prev, page }))
  }

  const setPageSize = (pageSize: number) => {
    setPagination((prev) => ({ ...prev, page: 1, pageSize }))
  }

  const setTotal = (total: number) => {
    setPagination((prev) => ({ ...prev, total, page: 1 }))
  }

  return {
    pagination,
    setPagination,
    setPage,
    setPageSize,
    setTotal,
    pageCount,
    paginationRange,
    paginatedData,
  }
}
